import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import RBACService from 'service/RBAC/RBACService';
import { RoleDetailResp } from 'domain/payload/response/RBAC/RoleDetailResp';
import { PrivilegeListResp } from 'domain/payload/response/RBAC/PrivilegeListResp';
import { useSelector } from 'react-redux';

const ManageRoleAccess: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { profile } = useSelector((state: any) => state.auth);
  const token = profile?.data?.token;
  const userId = profile?.data?.id;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [roleInfo, setRoleInfo] = useState<RoleDetailResp | null>(null);
  const [accessList, setAccessList] = useState<(PrivilegeListResp & { checked: boolean })[]>([]);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchRoleDetail = async (): Promise<RoleDetailResp | null> => {
    if (!token) {
      window.alert('Token tidak ditemukan. Silakan login kembali.');
      return null;
    }
    
    try {
      const res = await RBACService().getRoleDetail(token, id!);
      if (res?.data) {
        setRoleInfo(res.data);
        return res.data;
      }
      setRoleInfo(null);
      return null;
    } catch (error: any) {
      console.error('Error fetching role detail:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Terjadi kesalahan saat mengambil detail role';
      window.alert(`Error: ${errorMessage}`);
      setRoleInfo(null);
      return null;
    }
  };

  const fetchPrivilegeList = async (roleData?: RoleDetailResp | null, search?: string) => {
    if (!token) {
      window.alert('Token tidak ditemukan. Silakan login kembali.');
      return;
    }

    try {
      const res = await RBACService().getPrivilegeList(token, 1, 999999, search || null);
      const assignedIds = roleData?.priviledgeIds?.map((p: any) => p.id) || [];

      const privileges =
        res?.data?.map((item: PrivilegeListResp) => ({
          ...item,
          checked: assignedIds.includes(item.id),
        })) || [];

      setAccessList(privileges);
    } catch (error: any) {
      console.error('Error fetching privileges:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Terjadi kesalahan saat mengambil daftar privilege';
      window.alert(`Error: ${errorMessage}`);
      setAccessList([]);
    }
  };

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      const roleData = await fetchRoleDetail();
      await fetchPrivilegeList(roleData);
      setLoading(false);
    };
    init();
  }, [id]);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const roleData = await fetchRoleDetail();
    await fetchPrivilegeList(roleData, searchTerm);
    setLoading(false);
  };

  const handleToggle = (index: number) => {
    setAccessList((prev) =>
      prev.map((item, i) => (i === index ? { ...item, checked: !item.checked } : item))
    );
  };

  const handleSave = async () => {
    if (!token) {
      window.alert('Token tidak ditemukan. Silakan login kembali.');
      return;
    }

    if (!roleInfo || !id) {
      window.alert('Data role tidak ditemukan');
      return;
    }

    const selectedPrivileges = accessList.filter((item) => item.checked).map((item) => item.id);

    try {
      setSaving(true);
      await RBACService().saveRoleAccess(token, id, selectedPrivileges, userId);
      window.alert('Role access updated successfully.');
      navigate('/rbac');
    } catch (error: any) {
      console.error('Save failed:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to update role access. Please try again.';
      window.alert(`Error: ${errorMessage}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status"></div>
        <p className="mt-3">Loading data...</p>
      </div>
    );
  }

  return (
    <div className="page-wrapper">
      <div className="row">
        <div className="col-12">
          <div className="d-flex">
            <h5 style={{ flex: 1 }}>Manage Role Access</h5>
          </div>
        </div>

        <div className="col-12">
          <div className="p-4" style={{ background: 'white' }}>
            <div className="mb-4">
              <div className="col-md-6">
                <label className="text-muted small">Code</label>
                <p className="fw-bold">{roleInfo?.code || '-'}</p>
              </div>
              <div className="col-md-6">
                <label className="text-muted small">Name</label>
                <p className="fw-bold">{roleInfo?.name || '-'}</p>
              </div>
            </div>

            <div className="d-flex align-items-center mb-3 border-top py-3">
              <form className="d-flex" onSubmit={handleSearch}>
                <input
                  type="text"
                  className="form-control form-control-compact me-2"
                  placeholder="Search privilege..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <button type="submit" className="btn btn-primary px-4">
                  <i className="fa fa-search"></i>
                </button>
              </form>
            </div>
            <table className="table align-middle table-hover">
              <thead className="table-primary">
                <tr>
                  <th>Platform</th>
                  <th>Group</th>
                  <th>Code</th>
                  <th>Name</th>
                  <th className="text-center">Checklist</th>
                </tr>
              </thead>
              <tbody>
                {accessList.length > 0 ? (
                  accessList.map((item, index) => (
                    <tr key={item.id}>
                      <td>{item.applicationType}</td>
                      <td>{item.vgroup}</td>
                      <td>{item.code}</td>
                      <td>{item.name}</td>
                      <td className="text-center">
                        <input
                          type="checkbox"
                          checked={item.checked}
                          onChange={() => handleToggle(index)}
                        />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className="text-center py-3">
                      No privileges found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            <div className="d-flex justify-content-end mt-4">
              <button
                className="btn btn-outline-secondary me-2 px-4"
                onClick={() => navigate('/rbac')}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary px-4"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageRoleAccess;
